import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Image, { ImageLoader, ImageProps } from 'next/legacy/image';

import { STATIC_ASSETS_CACHE_KEY } from '../../util/assets/assets-key';
import { StaticAssetsApiService } from '../../domain/static-content/static-assets-api.service';
import { useSupportedImageFormat } from './use-supported-image-format';

export interface Asset {
  fileName: string;
  title: string;
  description?: string;
  url: string | null;
  width: number;
  height: number;
}

interface PhoenixImageProps extends ImageProps {
  className?: string;
  isAnimated?: boolean;
  staticTags?: string[];
  assetsKey?: string[];
}

export const PhoenixImage = ({
  className,
  isAnimated = false,
  layout = 'responsive',
  priority = false,
  staticTags,
  assetsKey = [STATIC_ASSETS_CACHE_KEY],
  ...imageProps
}: PhoenixImageProps) => {
  const { supportedFormat } = useSupportedImageFormat({ isAnimated });
  const cachedAssets = useQueryClient().getQueryData<Asset[]>(assetsKey);

  const { data } = useQuery<Asset[]>(assetsKey, StaticAssetsApiService.getAssetsFetcher(staticTags ?? []).fetcher, {
      enabled: !!staticTags && !cachedAssets?.length,
    }),
    assets = data ?? cachedAssets;

  const image = useMemo(() => findAssetByFileName(imageProps.src, assets), [imageProps.src, assets]);

  const blurDataURL = `${image?.url}?fm=${supportedFormat}&q=75&fit=thumb&w=50`;

  const loader: ImageLoader = ({ width, quality = 75 }) => {
    return `${image?.url}?fm=${supportedFormat}&w=${width}&q=${quality}`;
  };

  const size = {
    width: layout !== 'fill' ? imageProps.width || image?.width : undefined,
    height: layout !== 'fill' ? imageProps.height || image?.height : undefined,
  };

  return (
    <Image
      className={className}
      placeholder="blur"
      blurDataURL={blurDataURL}
      loader={loader}
      layout={layout}
      priority={priority}
      alt={image?.description}
      aria-hidden={!image?.description}
      {...size}
      {...imageProps}
    />
  );
};

const findAssetByFileName = (fileName: unknown, assets: Asset[] = []) => assets.find((asset) => fileName === asset.fileName);
